import React from "react";
import { createPortal } from "react-dom";
import { CARD_LANGUAGE_NAMES, CARD_LANGUAGES, GAMES, languagesForGames, legalLinks, MAX_LOCATIONS, RADIUS_OPTIONS } from "./data.jsx";
import { api } from "./api.jsx";
import { useApp } from "./store.jsx";
import { hrefFor, useBackCloses } from "./router.jsx";
import { LocationPicker } from "./location.jsx";

const { Wordmark, Tag, Button, IconButton, Icon, Badge, BottomNav, Toast, Dialog: SystemDialog, Select, Input, Textarea, Switch, Card, FluteDivider, Skeleton } = window.CardboardmarketDesignSystem_2a3239;

/**
 * EVERY DIALOG IN THE PRODUCT — the design system's, plus Back.
 *
 * The owner, 2026-10-23: Back closes any open dialog, the way it already
 * closed the Stock drawer ("I believe that they should be unified, yes").
 * Written here once (`useBackCloses`, router.jsx) rather than in thirty
 * dialogs, so every screen takes `Dialog` from this file and none from the
 * design system directly. `backCloses={false}` only for an overlay that
 * keeps a history entry of its own (the Stock sort drawer, timed to its slide).
 */
export function Dialog({ backCloses = true, ...props }) {
  useBackCloses(backCloses && props.open !== false, props.onClose);
  return <SystemDialog {...props} />;
}

/**
 * ASKED BEFORE IT IS DONE — the owner, 2026-10-23: "What is [necessary] is to
 * show a confirmation dialog before doing the action. I just deleted some
 * wants by mistake."
 *
 * One question for every action that destroys something or cannot be taken
 * back: what will happen and to how many, in the title ("Remove 12 wants?"),
 * the button named for the action rather than "OK", and Cancel focused, so a
 * second tap of the same spot, or Enter, keeps everything. `items` names what
 * is about to go, capped at a handful with the rest counted: "12" is a number
 * somebody has to trust they ticked correctly, and the one row in there by
 * accident is invisible until it is gone.
 */
export function ConfirmDialog({ title, description, confirmLabel, busyLabel, cancelLabel, confirmVariant, busy = false, items, onConfirm, onCancel, children, ...rest }) {
  const { t } = useApp();
  const shown = items ? items.slice(0, 6) : null;
  return (
    <ModalHost>
      <Dialog open closeLabel={t("Close")} width={440} role="alertdialog" aria-label={title} data-cbm="confirm"
        {...rest} data-confirm=""
        title={title} description={description}
        onClose={busy ? undefined : onCancel}
        footer={<>
          <Button variant="ghost" autoFocus data-cbm="confirm-cancel" disabled={busy} onClick={onCancel}>{cancelLabel || t("Cancel")}</Button>
          <Button data-cbm="confirm-go" variant={confirmVariant} disabled={busy} onClick={onConfirm}>{busy && busyLabel ? busyLabel : confirmLabel}</Button>
        </>}>
        {shown && shown.length ? (
          <ul style={{ margin: 0, paddingLeft: '1.1em', font: 'var(--type-body)', fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>
            {shown.map((name, i) => <li key={i}>{name}</li>)}
            {items.length > shown.length
              ? <li style={{ color: 'var(--text-muted)' }}>{t("and {n} more", { n: items.length - shown.length })}</li>
              : null}
          </ul>
        ) : null}
        {children}
      </Dialog>
    </ModalHost>
  );
}

/**
 * `ConfirmDialog` as a question an event handler can wait for:
 *
 *   const [confirm, confirmDialog] = useConfirm();
 *   onClick={async () => { if (await confirm({ title, confirmLabel })) remove(); }}
 *   … and `{confirmDialog}` rendered somewhere in the screen.
 *
 * Cancel, the X, the scrim and Back all answer false.
 */
export function useConfirm() {
  const [asking, setAsking] = React.useState(null);
  const confirm = React.useCallback((question) => new Promise((resolve) => {
    setAsking((previous) => { if (previous) previous.resolve(false); return { ...question, resolve }; });
  }), []);
  const answer = (yes) => { if (!asking) return; setAsking(null); asking.resolve(yes); };
  const { resolve, ...question } = asking || {};
  const element = asking
    ? <ConfirmDialog {...question} onConfirm={() => answer(true)} onCancel={() => answer(false)} />
    : null;
  return [confirm, element];
}

/* Dialog positions itself absolutely, so it needs a fixed full-viewport host. */
/**
 * The layer a dialog sits in — and NOTHING when there is no dialog.
 *
 * The guard used to be `if (!children)`, which is right for one child and wrong
 * for two: React hands over an ARRAY, and `[null, null]` is truthy. A screen with
 * two possible dialogs, both closed, therefore rendered a fixed full-screen div at
 * inset 0 over the whole page. Nothing looked wrong and nothing threw — every
 * click just landed on an invisible sheet, and the page appeared frozen.
 *
 * Flatten and look for something real, so the next caller with two dialogs does
 * not rediscover this.
 */
/**
 * Every dialog in the product, rendered into `document.body` rather than where it
 * was written.
 *
 * `position: fixed` and `z-index: 60` sound absolute and are not: a z-index only
 * competes inside its own stacking context. `position: sticky` CREATES one, and
 * `.cbm-rail__aside` is sticky (app.css), so a dialog opened from the browse
 * filter rail had its 60 scoped inside the rail — and the listing grid, which the
 * rail is painted under, covered it. From the header the same dialog worked,
 * because nothing sticky wrapped that one.
 *
 * The portal is the fix rather than a bigger number, because a bigger number only
 * moves the problem to the next sticky, transformed, filtered or `will-change`
 * ancestor somebody adds later — and that next time it will look like a fresh
 * bug rather than this one. Escaping to the body means a dialog cannot be trapped
 * by where it happens to be written, which is the whole job of a modal host.
 *
 * It also settles nesting: a dialog opened from inside the mobile filter sheet is
 * no longer a dialog inside a dialog, it is a sibling of it.
 */
let modalHostsOpen = 0;
export function ModalHost({ children }) {
  const real = React.Children.toArray(children).filter(Boolean);
  const open = real.length > 0;

  /*
   * THE ANDROID APP IS TOLD, so its pull-to-refresh stands down.
   *
   * REPORTED against the edit dialog once it grew tall enough to scroll: "we
   * have an issue with the scroll of the dialog where the native pull to
   * refresh interferes." The app wraps the WebView in a `SwipeRefreshLayout`,
   * which asks whether the child can scroll up — and the child is the whole
   * WebView, whose own scroll position stays at zero while an element INSIDE
   * the page scrolls. So scrolling back to the top of a dialog handed the
   * gesture to the refresh, and a reload threw away whatever was being edited.
   *
   * Nothing the page can do in CSS reaches that: `overscroll-behavior` governs
   * scroll chaining within the page, and this gesture is intercepted by a
   * native view before the page sees it.
   *
   * HERE rather than in each dialog, because every dialog in the product goes
   * through this host — which is also why it is safe: one mount, one unmount,
   * no counter to leak.
   *
   * Harmless everywhere else. `CardboardmarketAndroid` exists only inside the
   * app, and an older build of the app has no `setModalOpen`, so both fall
   * through to the behaviour they already had.
   */
  /*
   * COUNTED, now that a confirmation opens from inside a dialog (removing a
   * photo from the edit dialog): the inner host closing must not switch the
   * gesture back on under the dialog still open.
   */
  React.useEffect(() => {
    const bridge = typeof window !== 'undefined' ? window.CardboardmarketAndroid : null;
    if (!open || !bridge || typeof bridge.setModalOpen !== 'function') return undefined;
    modalHostsOpen += 1;
    bridge.setModalOpen(true);
    // Cleared on the way out as well as on the way in: a dialog closed by
    // navigating away must not leave the gesture switched off for good.
    return () => { modalHostsOpen -= 1; if (!modalHostsOpen) bridge.setModalOpen(false); };
  }, [open]);

  /*
   * THE PAGE BEHIND DOES NOT SCROLL WHILE A DIALOG IS OPEN.
   *
   * The owner's own diagnosis, and a better one than the first: "I believe the
   * issue might be related with the fact that I can scroll the underlying page
   * even with the modal on top." Quite right, and it is the mechanism behind
   * the pull-to-refresh as well — a scroll that runs out at the top of the
   * dialog CHAINS to the page behind it, and a page already at its own top
   * hands the gesture to the refresh.
   *
   * It is also a defect on its own. A modal that lets the page move underneath
   * it is a modal in appearance only: somebody scrolling a long dialog on a
   * phone watches the screen behind slide about, and closing it leaves them
   * somewhere they never chose to be.
   *
   * `overflow: hidden` on <html>, which is what the viewport takes its
   * overflow from — and ONLY on <html>. Restored to whatever it said before
   * rather than to a hardcoded empty string, so a page that sets its own
   * overflow is not quietly rewritten by closing a dialog.
   *
   * NOT ON <body> AS WELL, which it once was. Once <html> is not `visible`,
   * <body>'s overflow is no longer handed to the viewport: `hidden` there makes
   * <body> a scroll container of its own, and every `position: sticky` element
   * starts sticking inside an unscrolled <body> while the window stays
   * scrolled. REPORTED on pre: the back office's rail (`.sh-side`, sticky and
   * 100vh tall) slid up by the scroll offset behind a dialog opened from
   * halfway down the store page, ending partway down the screen.
   * `write-back.spec.ts` measures it.
   */
  React.useEffect(() => {
    if (!open || typeof document === 'undefined') return undefined;
    const root = document.documentElement;
    const before = root.style.overflow;
    root.style.overflow = 'hidden';
    return () => { root.style.overflow = before; };
  }, [open]);

  if (!open) return null;
  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 60 }}>{real}</div>,
    document.body,
  );
}

/**
 * Exported because Browse opens one too — see FilterControls, where a Distance
 * filter with no point to measure from hands this over instead of pretending.
 *
 * Shared rather than copied: setting a point and setting a radius is one decision
 * taken in one place, and a second implementation of it would be the one that
 * forgets the rule in the footer below.
 *
 * `open` belongs to the caller, which is all the state there is, and two of these
 * cannot be open at once for the ordinary reason: whichever opens first covers
 * the screen, so the control that would open the other cannot be reached.
 *
 * IT NOW HOLDS A LIST, AND STILL HOLDS ONE MAP.
 *
 * A trader has more than one honest answer to "where are you trading from?" —
 * home in the week, a parents' flat at the weekend — so the saved ones are picked
 * from a list and the map is what you compose a new one with. Two views, not two
 * components: this dialog is opened from the header and from the browse filter
 * rail, and splitting it would put the same decision in two places again.
 *
 * A guest has no account to hang a list on, so they get exactly what they always
 * got: the map, the radius, and Save.
 */
export function LocationDialog({ open, onClose }) {
  const {
    radius, setRadius, point, setPoint, trader, notify, t,
    locations, activeLocation, addLocation, editLocation, activateLocation, removeLocation,
  } = useApp();
  const [p, setP] = React.useState(point);
  const [r, setR] = React.useState(radius);
  /*
   * What the dialog is doing right now, as one value rather than three booleans.
   *
   *   null                       the list
   *   { id: null }               composing a new location
   *   { id, label }              editing a saved one — its name, its pin, or both
   *
   * A guest and an account with nothing saved never see the list, because a list
   * of one thing you cannot switch away from is furniture.
   */
  const [composing, setComposing] = React.useState(null);
  const [label, setLabel] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [confirm, confirmDialog] = useConfirm();

  const saved = trader ? locations : [];
  const listing = saved.length > 0 && composing === null;
  const full = saved.length >= MAX_LOCATIONS;

  /*
   * Start from what is actually saved, every time it opens.
   *
   * `useState(point)` reads once, at mount — and this dialog is mounted inside the
   * header before /api/me has answered, so `p` stayed null forever. The map came
   * up empty for somebody who already had a location, and because Save is disabled
   * without a point, they could not even change the RADIUS without redefining
   * where they live first.
   *
   * Re-syncing on open also means a dialog abandoned halfway does not reopen
   * showing the half-finished state from last time — which now includes a
   * half-typed name and a pin dropped for a location nobody saved.
   */
  React.useEffect(() => {
    if (!open) return;
    setP(point);
    setR(radius);
    setComposing(null);
    setLabel('');
  }, [open, point && point.join(','), radius]);

  /** Anything that talks to the server, with one place to say when it refused. */
  const attempt = async (work, said) => {
    setBusy(true);
    try {
      await work();
      if (said) notify(said);
      return true;
    } catch (e) {
      notify(e.message, 'caution');
      return false;
    } finally { setBusy(false); }
  };

  const compose = (location) => {
    setComposing(location ? { id: location.id, label: location.label } : { id: null });
    setLabel(location ? location.label : '');
    setP(location ? [location.lat, location.lng] : null);
  };

  /*
   * Saving the thing being composed.
   *
   * The label is optional on the way in and stays optional here: left blank, the
   * SERVER names it — in the language the account is set to, with a generic noun
   * that is never a place name. Sending the placeholder's words instead would ship
   * whichever language the browser happened to be showing back as stored data.
   */
  const saveComposed = async () => {
    const named = label.trim();
    /*
     * A radius on its own is still a save, and that rule predates all of this.
     *
     * "How far will I travel" is a real answer somebody can give before they have
     * said where from, and refusing it until a point exists made the radius
     * hostage to the map. It applies to an account exactly as it applies to a
     * guest — a trader who has never placed themselves is the person most likely
     * to be adjusting one without the other.
     */
    if (!p) { setRadius(r); notify(t("Radius set to {radius}.", { radius: r })); onClose(); return; }
    // A guest has no account to hang a named location on, so their point lives on
    // their own device, exactly as it always has.
    if (!trader) { setRadius(r); setPoint(p); notify(t("Location and radius saved.")); onClose(); return; }
    // Null when an account with nothing saved goes straight to the map — there is
    // no list to have come from, so there is nothing being edited either.
    const editingId = composing && composing.id;
    const done = editingId
      ? await attempt(
        () => editLocation(editingId, { ...(named ? { label: named } : {}), lat: p[0], lng: p[1] }),
        t("Location saved."),
      )
      : await attempt(
        () => addLocation({ ...(named ? { label: named } : {}), lat: p[0], lng: p[1] }),
        t("Location saved."),
      );
    if (!done) return;
    if (r !== radius) setRadius(r);
    if (saved.length) setComposing(null); else onClose();
  };

  const rows = saved.map((l) => (
    <li key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
      {/*
        The row itself SWITCHES to that location, because that is what somebody
        opening this list came to do nine times out of ten. Editing and deleting
        are deliberate side actions and look like it.
      */}
      <button
        type="button"
        disabled={busy}
        aria-pressed={l.active}
        onClick={() => { if (!l.active) attempt(() => activateLocation(l.id), t("Now trading from {label}.", { label: l.label })); }}
        style={{
          flex: 1, display: 'flex', alignItems: 'center', gap: 'var(--space-3)', minHeight: 40,
          padding: '8px 10px', textAlign: 'left', cursor: l.active ? 'default' : 'pointer',
          font: 'var(--type-ui)', color: 'var(--text-body)',
          background: l.active ? 'var(--orange-100)' : 'var(--white)',
          border: `1px solid ${l.active ? 'var(--orange-300)' : 'var(--border-hairline)'}`,
          borderRadius: 'var(--radius-sm)',
        }}
      >
        <Icon name="map-pin" size={14} />
        <span style={{ flex: 1 }}>{l.label}</span>
        {l.active ? <Icon name="check" size={15} /> : null}
      </button>
      <IconButton icon="pencil" size="sm" variant="ghost" label={t("Rename or move {label}", { label: l.label })} disabled={busy} onClick={() => compose(l)} />
      <IconButton
        icon="trash-2" size="sm" variant="ghost" label={t("Delete {label}", { label: l.label })} disabled={busy}
        /*
          Deleting the one you are trading from is allowed, and the server decides
          what happens next: the oldest remaining location takes over, and deleting
          the last leaves the account unplaced. Refusing instead would mean a trader
          who moved house cannot delete the address they moved out of until they
          have saved the new one, which is backwards.
        */
        onClick={async () => {
          const yes = await confirm({
            title: t("Delete “{label}”?", { label: l.label }),
            description: t("Its point and its name are deleted. Setting it again means dropping the pin again."),
            confirmLabel: t("Delete location"),
          });
          if (yes) attempt(() => removeLocation(l.id), t("Location deleted."));
        }}
      />
    </li>
  ));

  return (
    <ModalHost>
      {open ? (
        <Dialog open
          /*
            A modal that says it is one. The design system's Dialog draws the box
            and leaves the semantics to its caller, and this one now holds a list
            somebody navigates rather than a single form — so it is worth the role
            and a name of its own.
          */
          role="dialog" aria-label={t("Where are you trading from?")}
          closeLabel={t("Close")} title={t("Where are you trading from?")} description={t("We show listings you could walk or ride to. Your exact point is never shown to another trader — they only ever see a distance.")} onClose={onClose} width={420}
          footer={listing
            ? <><Button variant="ghost" onClick={onClose}>{t("Cancel")}</Button><Button onClick={() => { setRadius(r); notify(t("Radius set to {radius}.", { radius: r })); onClose(); }} disabled={busy || r === radius}>{t("Save")}</Button></>
            : <>
              <Button variant="ghost" onClick={() => (saved.length ? setComposing(null) : onClose())}>{t("Cancel")}</Button>
              <Button onClick={saveComposed} // Not `disabled={!p}`. A radius is a useful thing to set on its own —
                // it is what "how far will I travel" means — and refusing to save
                // one until a location exists made the radius hostage to the map.
                disabled={busy || (!p && r === radius)}>{trader && p ? t("Save location") : t("Save")}</Button>
            </>}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
            {listing ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                <div className="cbm-label">{t("Your locations")}</div>
                <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>{rows}</ul>
                {full
                  /* The number comes from the constant rather than being spelled
                     out, so the sentence cannot outlive the cap it describes. */
                  ? <span className="cbm-meta" style={{ color: 'var(--text-muted)' }}>{t("You can save up to {n} locations. Delete one to add another.", { n: MAX_LOCATIONS })}</span>
                  : <Button variant="outline" size="sm" iconLeft="plus" disabled={busy} onClick={() => compose(null)}>{t("Add a location")}</Button>}
              </div>
            ) : (
              <>
                {trader ? (
                  <Input
                    label={t("Name for this location")}
                    /*
                      A placeholder, not a filled-in value, and the difference is
                      i18n rather than taste: a translated string put into state
                      freezes in whichever language loaded first, so the default
                      lives on the server and this only SHOWS what it will be.
                    */
                    placeholder={t("My point")}
                    maxLength={40}
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    hint={t("Only you ever see this.")}
                  />
                ) : null}
                <LocationPicker value={p} onChange={setP} />
              </>
            )}
            <Select label={t("Radius")} options={RADIUS_OPTIONS} value={r} onChange={setR} hint={t("Median meetup distance here is 1.6 km.")} />
          </div>
        </Dialog>
      ) : null}
      {open ? confirmDialog : null}
    </ModalHost>
  );
}

/**
 * The chip in the header, which finally says WHERE as well as how far.
 *
 * It showed the radius alone — "5 km" — next to a pin, which reads as a place and
 * is not one: it is how far you will travel, and it was the only thing the control
 * ever said. With a name beside it the pair is legible at a glance, and it is the
 * one place the label earns its keep on every screen.
 *
 * The radius stays, because a chip that named a location and dropped the distance
 * would take away the thing this control has always been used for.
 */
function LocationChip({ onClick }) {
  const { radius, point, activeLocation, t } = useApp();
  return (
    <button type="button" onClick={onClick} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: '1px solid var(--border-hairline)', borderRadius: 'var(--radius-pill)', padding: '6px 12px', cursor: 'pointer', color: 'var(--text-body)', font: 'var(--type-ui)', minHeight: 32 }}>
      <Icon name="map-pin" size={14} />
      <span>{point
        // A guest has a point and no account to name it on, so they get what they
        // always got.
        ? (activeLocation ? `${activeLocation.label} · ${radius}` : radius)
        : t("Set your location")}</span>
      <Icon name="chevron-down" size={13} />
    </button>
  );
}

/**
 * `personal` means the tab is about YOUR account and is hidden from a guest.
 *
 * Hiding beats showing-then-asking: a visible Trades tab is a promise that there is
 * something behind it, and spending a tap to be told "sign in first" is a worse
 * introduction than simply not offering what does not apply yet.
 */
// Module scope: these hold the English KEY. t() here would run once at load,
// before a locale exists, and never again when somebody switches language.
const NAV = [
  { value: 'browse', label: 'Browse' },
  { value: 'trades', label: 'Trades', personal: true },
  { value: 'inbox', label: 'Inbox', personal: true },
];

/**
 * A standing reminder for an unverified account.
 *
 * Posting is blocked until the address is confirmed, and until now the app never
 * said so anywhere — someone whose confirmation email went to spam had no way to
 * ask for another, and no explanation for why posting kept refusing them. A bar
 * that is always visible while it applies, and disappears the moment it does not.
 */
export function VerifyBanner() {
  const { trader, notify, serverConfig, t } = useApp();
  const [sending, setSending] = React.useState(false);
  const [sent, setSent] = React.useState(false);
  // Nothing to say when confirmation is not a gate on this deployment. The banner's
  // whole text is about being blocked from posting; showing it when nothing is
  // blocked would invite somebody to wait for a message that gates nothing — and
  // while mail is broken, to wait for one that will not arrive at all.
  if (!serverConfig.requireEmailVerification) return null;
  if (!trader || trader.emailVerified) return null;

  const resend = async () => {
    setSending(true);
    try {
      const r = await api.resendVerification();
      // The server says whether the mail actually left. Reporting "Sent" when it
      // did not is a lie told to the one person who could act on the truth.
      if (r && r.emailSent === false) {
        notify(t("We could not send it just now. Try again in a few minutes."), 'caution');
        return;
      }
      setSent(true);
      notify(t("Sent. Check your inbox — and your spam folder."));
    } catch (e) {
      notify(e.message, 'caution');
    } finally { setSending(false); }
  };

  return (
    // Full-bleed background, contents on the same column as everything else.
    // A bar whose text starts at the window edge while the header beneath it starts
    // at the content column reads as a piece of a different page.
    <div style={{ background: 'var(--surface-sunken)', borderBottom: '1px solid var(--border-hairline)' }}>
      <div className="cbm-bar__in">
        <Icon name="clock" size={16} />
        <span style={{ flex: 1, minWidth: 220, font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--text-body)' }}>
          {t("Confirm your email to post cards. Check your inbox — and your spam folder.")}
        </span>
        <Button size="sm" variant="outline" onClick={resend} disabled={sending || sent}>
          {sending ? t("Sending…") : sent ? t("Sent") : t("Send it again")}
        </Button>
      </div>
    </div>
  );
}

/**
 * A BACKGROUND IMPORT, FROM ANYWHERE IN THE PRODUCT.
 *
 * Asked for: "it would be nice if the user had some kind of indication of state
 * and/or progress of the background task somewhere."
 *
 * Somewhere has to mean off the import screen. The slow path's whole offer is
 * "you can leave this page" — so the one place the trader was guaranteed NOT to
 * be while it ran was the only place that knew anything about it. Queue a file,
 * go and browse, and the import might as well not have been happening.
 *
 * It rides the pulse the header already polls, so it costs no request.
 *
 * TWO STATES, ONE STRIP. Running says how far it has got; finished says it
 * finished and offers the result. They are the same sentence to the person
 * reading — "what happened to my file" — and splitting them would make the
 * moment it lands a new thing appearing rather than a number settling.
 *
 * It does not say what went wrong on a failure, and that is deliberate: the
 * reason needs the room the import screen has, and a strip that tried would
 * either truncate it or push the page down. It says it needs looking at.
 */
export function ImportStrip() {
  const { runningImport: job, go, route, t } = useApp();
  if (!job) return null;
  // Not on the screen that already says all of this, in more detail and with
  // somewhere to go next. A strip repeating the page underneath it is noise.
  if (route.name === 'import') return null;

  const running = job.status === 'pending' || job.status === 'running';
  const failed = job.status === 'failed';
  const wants = job.kind === 'wants';

  return (
    <div
      /* A stable handle for the tests. The strip's own words change with the
         job's state and with the locale, so asserting on them means asserting
         on whichever state the worker happened to reach — which is how the
         "it must stand down on /import" case passed against a build that did
         not stand down at all. */
      data-cbm="import-strip"
      style={{
        background: failed ? 'var(--surface-danger-subtle, var(--surface-sunken))' : 'var(--surface-sunken)',
        borderBottom: '1px solid var(--border-hairline)',
      }}>
      <div className="cbm-bar__in">
        <Icon
          name={running ? 'loader' : failed ? 'alert-triangle' : 'check'}
          size={16}
          /* The spinner has to actually spin. A still glyph beside "working on
             it" is what "stuck" looks like, which is how it was reported. */
          className={running ? 'cbm-spin' : undefined}
        />
        <span role="status" aria-live="polite" style={{ flex: 1, minWidth: 220, font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--text-body)' }}>
          {running
            ? (job.rowsRead
              ? t("Reading your file. {n} rows so far.", { n: job.rowsRead.toLocaleString() })
              : t("Reading your file in the background."))
            : failed
              ? t("Your import did not go through.")
              : wants
                ? t("Your wants are imported.")
                : t("Your listings are imported.")}
        </span>
        <Button size="sm" variant="outline" onClick={() => go('import')}>
          {running ? t("See it") : t("See the result")}
        </Button>
      </div>
    </div>
  );
}

/**
 * A draw that is open, said wherever somebody happens to be.
 *
 * IT USED TO LIVE IN BROWSE'S FILTER RAIL, and that rail is `cbm-wide` — it
 * does not render at all below 1000px. So on a phone, which is how most of
 * this product is used, an open draw was invisible: not buried, absent. The
 * owner reported it as "not shown anywhere" and then corrected himself having
 * found the box on a desktop, which is exactly the shape of that bug.
 *
 * A STRIP RATHER THAN A CARD IN A SCREEN, for the reason `ImportStrip` is one:
 * the thing it announces is true wherever you are, and a screen that has to
 * remember to mention it is a screen that will stop.
 *
 * DISMISSIBLE, AND PER DRAW. A single "hidden" flag would silence the next
 * campaign too, months later, for everybody who ever closed this one — which
 * is how a promotion quietly stops reaching the people most likely to have
 * read the last one. Remembered in `localStorage`, which can throw, so every
 * read and write is guarded and the strip simply shows when storage is gone.
 *
 * GUESTS TOO. They cannot invite anybody yet — a code needs an account — but
 * a draw is a reason to make one, and the draw's own page asks them properly.
 */
export function DrawStrip() {
  const { liveCampaign, unrevealed, checkResult, route, go, trader, t } = useApp();
  /*
   * A RESULT WAITING OUTRANKS A DRAW STILL RUNNING. Somebody with an unwatched
   * result has a question the product can answer right now; an open campaign is
   * an invitation they can take up any time this week. Two strips stacked would
   * be the shell shouting.
   */
  const c = unrevealed || liveCampaign;
  const waiting = Boolean(unrevealed);
  const key = c ? `cbm.draw.hidden.${c.id}` : null;
  /*
   * READ BEFORE THE FIRST PAINT, not in an effect afterwards.
   *
   * It used to start `false` and correct itself on mount, which meant a strip
   * somebody had dismissed came back for one frame on every page load and then
   * vanished — the shell visibly resizing under the content below it. The test
   * for this caught it about one run in three and looked like a flake, because
   * a retrying assertion that happens to poll during that frame sees it and one
   * that polls after it does not.
   *
   * A lazy initializer runs during the first render instead, so the dismissed
   * strip is never drawn at all. The effect below still exists for the case the
   * initializer cannot cover: the campaign changing while the page is open.
   */
  const read = (k) => {
    if (!k) return false;
    try { return window.localStorage.getItem(k) === '1'; } catch (e) { return false; }
  };
  const [hidden, setHidden] = React.useState(() => read(key));

  React.useEffect(() => { setHidden(read(key)); }, [key]);

  if (!c || hidden) return null;
  // Not on the screens that already say all of this, with somewhere to go next.
  // A strip repeating the page underneath it is noise — the same rule ImportStrip
  // follows on /import.
  if (route.name === 'campaign' || route.name === 'invite' || route.name === 'invited') return null;
  /*
   * And on browse while a result is waiting, because the full banner is up
   * there. Only then: with a draw merely running, browse is exactly where the
   * strip earns its place.
   */
  if (waiting && route.name === 'browse') return null;

  const daysLeft = Math.max(0, Math.ceil((new Date(c.endsAt) - Date.now()) / 86400000));
  const hide = () => {
    setHidden(true);
    try { window.localStorage.setItem(key, '1'); } catch (e) { /* a session-long dismissal is still one */ }
  };

  return (
    <div
      data-cbm="draw-strip"
      style={{ background: 'var(--surface-sunken)', borderBottom: '1px solid var(--border-hairline)' }}>
      <div className="cbm-bar__in">
        <Icon name="star" size={16} />
        <span style={{ flex: 1, minWidth: 220, font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--text-body)' }}>
          {/*
            THE TITLE IS THE WAY TO THE RULES.

            Reported: "I can't click it or anything to know more on how does it
            work. I only see a button that says Invite a friend." Which was
            true for a signed-in trader — their button goes to /invite, so the
            strip named a prize and offered no way to find out what winning it
            takes. A headline that announces something and cannot be opened is
            a dead end.

            A real link, not a clickable div: it gets an address, so it can be
            middle-clicked and its destination shows in the status bar.
          */}
          <a
            href={hrefFor({ name: 'campaign', id: c.id })}
            onClick={(e) => { e.preventDefault(); go('campaign', { id: c.id }); }}
            style={{ color: 'var(--text-strong)', font: 'var(--type-ui)', fontSize: 'var(--text-xs)', textDecoration: 'underline', textUnderlineOffset: 3 }}
          >
            {c.title}
          </a>
          {' · '}
          {waiting
            ? t("The draw is done")
            : daysLeft === 0 ? t("Closes today.") : t("{n} days left", { n: daysLeft })}
        </span>
        {/*
          And the same destination as a plain control, because underlined text
          inside a notification bar is not obviously a link to everybody — his
          own words were "maybe an information icon".

          Only when the button beside it goes SOMEWHERE ELSE. A guest's button
          already says "See how" and lands on the same page; two controls one
          tap apart doing the identical thing is clutter, not clarity.
        */}
        {/*
          ONE GROUP, so they wrap as a unit or not at all. Reported as the banner
          looking broken on a phone: the text beside them is `flex:1`, so the
          info icon finished the first row while the button and the dismiss went
          to a second one — three controls with one stranded a row above the
          other two. Loose children in a wrapping row break wherever they happen
          to run out of space.
        */}
        <div className="cbm-bar__acts">
          {trader && !waiting ? (
            <IconButton icon="info" label={t("How the draw works")} size="sm" variant="ghost"
                        onClick={() => go('campaign', { id: c.id })} />
          ) : null}
          {waiting ? (
            /* The one thing worth doing, and the strip is not dismissible while
               it is offered — a result you have not seen is not clutter. */
            <Button size="sm" iconLeft="star" onClick={() => checkResult(c.id)}>{t("Check if I won")}</Button>
          ) : (
            <>
              <Button size="sm" variant="outline" onClick={() => go(trader ? 'invite' : 'campaign', trader ? undefined : { id: c.id })}>
                {trader ? t("Invite a friend") : t("See how")}
              </Button>
              <IconButton icon="x" label={t("Hide")} size="sm" variant="ghost" onClick={hide} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Switching language.
 *
 * In the header rather than buried in settings: a guest who lands on the wrong
 * language cannot reach settings — that screen needs an account — and somebody
 * reading a language they do not speak is exactly the person least able to hunt
 * for the control.
 *
 * The labels are each written in their own language, which is the one convention
 * everybody understands regardless of what they currently see.
 */
/**
 * CARDMARKET'S OWN WORDMARK, because the two names blur.
 *
 * The owner: "let's try to use the Cardmarket logo to reference it instead of,
 * or along with, the name. Because Cardmarket and Cardboardmarket are very
 * similar, and with their logo we avoid confusion."
 *
 * He is right, and it is worst exactly where it matters most — a warning
 * somebody skims, telling them this shop's stock is really kept on the OTHER
 * site. Read quickly, "Cardmarket" and "Cardboardmarket" are the same shape.
 * Their wordmark is navy and set in a different face, so it cannot be mistaken
 * for ours at any speed.
 *
 * THE ASSET IS THEIRS, published by them for this purpose —
 * help.cardmarket.com/en/Downloads — and supplied by the owner rather than
 * taken. Trimmed of its white margins and stored at 48px tall, which is three
 * times the size it is ever drawn at; the original is 3,125px wide and would be
 * a quarter of a megabyte of nothing beside a line of text.
 *
 * `alt` DEFAULTS TO THE NAME AND IS EMPTIED WHERE THE SENTENCE ALREADY SAYS IT.
 * A logo next to the word "Cardmarket" is decoration for a screen reader and a
 * second reading of the same word; a logo standing in PLACE of the word is the
 * only thing carrying it, and has to be announced.
 */
/*
 * A TRADER'S CARDMARKET PROFILE, UNDER A GAME WHERE WE HAVE ONE.
 *
 * Two corrections, in order. `Magic` was hard-coded, so a One Piece seller's
 * profile opened under somebody else's game; the owner then asked for the
 * game-less `…/en/Users/DRAGONCT`, "since Cardmarket will automatically
 * redirect to the game selected by the user". It does — for somebody logged
 * into Cardmarket. A guest gets a 404 (the owner, 2026-10-17).
 *
 * So the game goes back, and it is the RIGHT one: beside a listing, the
 * listing's own game; on a trader's page, the one the API chose from the games
 * the trader selected (`cardmarketGame`, the first in the product's order).
 * Only where there is no game, or none Cardmarket names that we could check,
 * is it the short form — which still works for anybody signed in there.
 *
 * Cardmarket's path names, each one checked against a page of theirs that
 * answered 200 (the Internet Archive's copies, 2025-10 to 2026-06: their site
 * refuses a script with a Cloudflare challenge). Gundam is left out: no copy
 * of `/en/Gundam` that answered 200 could be found to check it against.
 *
 * `/en/` STAYS. That is Cardmarket's own interface language, not ours, and they
 * do not serve a locale-free path; a Spanish reader gets Cardmarket in English
 * either way, which is what the link always did.
 */
const CARDMARKET_GAME_PATHS = {
  magic: 'Magic',
  pokemon: 'Pokemon',
  yugioh: 'YuGiOh',
  onepiece: 'OnePiece',
  lorcana: 'Lorcana',
  riftbound: 'Riftbound',
};

export const cardmarketProfileUrl = (username, game) => {
  const path = CARDMARKET_GAME_PATHS[game];
  return `https://www.cardmarket.com/en/${path ? `${path}/` : ''}Users/${encodeURIComponent(username)}`;
};

export function CardmarketMark({ height = 14, alt = 'Cardmarket', stacked = false }) {
  return (
    <img
      src={stacked ? "/brand/cardmarket-mark.png" : "/brand/cardmarket.png"}
      alt={alt}
      style={{ height, width: 'auto', display: 'inline-block', verticalAlign: 'text-bottom' }}
    />
  );
}

/*
 * THE STACKED MARK, FOR A PLACE A SENTENCE WOULD NOT FIT.
 *
 * Reported: "In the rows, the horizontal logo for cardmarket is very bad."
 * It was — the wordmark is 341×48, so beside a price at 10px tall it is 71px of
 * wordmark squeezed into a column that has about 20 to spare, and the letters
 * stop being letters.
 *
 * Their own vertical lockup, supplied by the owner from the same downloads page
 * as the wordmark. Trimmed of its margins and stored at 72px tall against the
 * ~18px it is drawn at, so it stays sharp on a dense screen. 69×72 and 3.6 kB —
 * smaller than the wordmark it replaces here, because it is mostly the symbol.
 *
 * NOT A REPLACEMENT FOR THE WORDMARK. Where there is room for a sentence —
 * "Also sells on", the follow button, the dialog — the wordmark is still the
 * right thing, and it is the half of their branding that reads as a NAME. This
 * is for the places that need a stamp.
 */

/**
 * CARDTRADER'S WORDMARK, ON THE GROUND IT WAS DRAWN FOR.
 *
 * The owner, after I reported that the file he sent is white-on-dark: "they
 * don't seem to have a dark version, so maybe we could wrap it inside a box
 * with background #142544, which is the one they use in their site."
 *
 * That is the right answer and it is why this draws a chip. Their asset puts
 * "TRADER" in #fff and only "CARD" and the triangle in blue, so on our kraft
 * card half the name disappears — rendered on white, you read "CARD" and a
 * triangle. The alternative was recolouring somebody else's trademark, which
 * is worse than presenting it on its own background.
 *
 * #142544 IS THEIRS, taken from their site rather than guessed, so the chip is
 * the surface the mark expects rather than a colour we invented for it.
 *
 * The chip is why this exists at all instead of a second <img>: a bare dark
 * rectangle beside a line of body text is a deliberate thing, not a default,
 * and it should look the same everywhere it appears.
 */
export function CardTraderMark({ height = 14, alt = 'CardTrader' }) {
  return (
    <span
      style={{
        display: 'inline-flex', alignItems: 'center',
        background: '#142544', borderRadius: 'var(--radius-sm)',
        padding: '3px 6px', verticalAlign: 'text-bottom',
      }}
    >
      <img src="/brand/cardtrader.svg" alt={alt} style={{ height, width: 'auto', display: 'block' }} />
    </span>
  );
}

export function LanguageSwitch({ compact = false }) {
  const { locale, setLocale } = useApp();
  const options = [['es-ES', 'ES', 'Español'], ['en-US', 'EN', 'English']];

  // Narrow screens get one button instead of two, because the header there is
  // already carrying a location control and a way in. It shows the language you
  // would GET, not the one you are in — and says so out loud, since a lone "EN"
  // is genuinely ambiguous about which of the two it means.
  if (compact) {
    const other = options.find(([value]) => value !== locale) || options[0];
    return (
      <button
        type="button"
        onClick={() => setLocale(other[0])}
        aria-label={locale === 'es-ES' ? `Cambiar a ${other[2]}` : `Switch to ${other[2]}`}
        title={locale === 'es-ES' ? `Cambiar a ${other[2]}` : `Switch to ${other[2]}`}
        style={{
          border: '1px solid var(--border-hairline)', borderRadius: 'var(--radius-pill)',
          background: 'none', cursor: 'pointer', padding: '0 10px', minHeight: 32, minWidth: 40,
          font: 'var(--type-meta)', letterSpacing: 'var(--tracking-label)', color: 'var(--text-muted)',
        }}
      >
        {other[1]}
      </button>
    );
  }

  return (
    <div role="group" aria-label="Idioma / Language" style={{ display: 'inline-flex', border: '1px solid var(--border-hairline)', borderRadius: 'var(--radius-pill)', overflow: 'hidden' }}>
      {options.map(([value, label, name]) => (
        <button
          key={value}
          type="button"
          onClick={() => setLocale(value)}
          aria-pressed={locale === value}
          title={name}
          style={{
            border: 0, cursor: 'pointer', padding: '5px 10px', minHeight: 30,
            font: 'var(--type-meta)', letterSpacing: 'var(--tracking-label)',
            background: locale === value ? 'var(--ink-900)' : 'transparent',
            color: locale === value ? 'var(--kraft-50)' : 'var(--text-muted)',
          }}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

export function Header() {
  const { route, go, unread, pendingTrades, trader, requireAuth, t } = useApp();
  const [loc, setLoc] = React.useState(false);
  return (
    <header className="cbm-head">
      <div className="cbm-head__in">
        <button onClick={() => go('browse')} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex' }} aria-label={t("Cardboardmarket home")}>
          <Wordmark size={18} />
        </button>
        <nav className="cbm-nav cbm-desk" style={{ flex: 1 }}>
          {NAV.filter((n) => trader || !n.personal).map((n) => (
            <button key={n.value} data-on={route.name === n.value ? '1' : '0'} onClick={() => go(n.value)}>
              {t(n.label)}
              {n.value === 'inbox' && unread ? <span style={{ marginLeft: 6, font: 'var(--type-meta)', color: 'var(--orange-600)' }}>{unread}</span> : null}
              {n.value === 'trades' && pendingTrades ? <span style={{ marginLeft: 6, font: 'var(--type-meta)', color: 'var(--orange-600)' }}>{pendingTrades}</span> : null}
            </button>
          ))}
        </nav>
        <div style={{ flex: 1 }} className="cbm-mob" />
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
          <span className="cbm-wide"><LocationChip onClick={() => setLoc(true)} /></span>
          <span className="cbm-desk"><Button iconLeft="plus" onClick={() => { if (requireAuth('post a card')) go('post'); }}>{t("Post a card")}</Button></span>
          <span className="cbm-narrow"><IconButton icon="map-pin" label={t("Set your radius")} variant="ghost" onClick={() => setLoc(true)} /></span>
          <span className="cbm-wide"><LanguageSwitch /></span>
          <span className="cbm-narrow"><LanguageSwitch compact /></span>
          {trader
            ? <IconButton icon="user" label={t("Your profile")} variant={route.name === 'you' ? 'solid' : 'outline'} onClick={() => go('you')} />
            // A guest needs an obvious way in that is an invitation, not a wall.
            : <Button variant="outline" onClick={() => requireAuth('do that')}>{t("Sign in")}</Button>}
        </div>
      </div>
      <LocationDialog open={loc} onClose={() => setLoc(false)} />
    </header>
  );
}

export function TabBar() {
  const { route, go, unread, pendingTrades, trader, requireAuth, t } = useApp();
  const bar = React.useRef(null);

  /*
   * THE BAR MEASURES ITSELF, and publishes its height for anything that has to
   * sit on top of it.
   *
   * `--app-nav-space` was a written-down 96px, and the conversation screen
   * anchored its bottom edge to it. The real bar is shorter than that on a
   * phone, so the composer floated above it with a strip of page showing
   * through — reported as "the input should sit on top of the bottom bar, not
   * overlapping it", which is exactly right and was neither.
   *
   * A second hard-coded number would have been the same bug with a different
   * value: the bar's height depends on the type size, the badges, and the
   * device's safe-area inset at the bottom, none of which this file knows. So it
   * asks the element. `app.css` already refuses to compute this layout by
   * subtraction — "ANCHOR, DO NOT SUBTRACT" — and this is the same refusal.
   */
  React.useEffect(() => {
    const el = bar.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const publish = () => {
      document.documentElement.style.setProperty('--app-nav-h', `${Math.round(el.getBoundingClientRect().height)}px`);
    };
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    return () => { ro.disconnect(); };
  }, []);

  return (
    <div ref={bar} className="cbm-mob" style={{ position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 30 }}>
      <BottomNav
        value={route.name}
        // Post is the one action a guest is offered here: it is the invitation, so
        // it asks for an account rather than disappearing.
        onChange={(v) => { if (v === 'post' && !requireAuth('post a card')) return; go(v); }}
        items={[
          { value: 'browse', label: t("Browse"), icon: 'search' },
          { value: 'post', label: t("Post"), icon: 'plus' },
          // Hidden rather than shown-and-guarded: an offered tab is a promise that
          // there is something behind it.
          ...(trader ? [
            { value: 'trades', label: t("Trades"), icon: 'arrow-left-right', badge: pendingTrades || undefined },
            { value: 'inbox', label: t("Inbox"), icon: 'message-circle', badge: unread || undefined },
          ] : []),
        ]}
      />
    </div>
  );
}

export function ToastHost() {
  const { toast, clearToast } = useApp();
  if (!toast) return null;
  return (
    <div style={{ position: 'fixed', left: '50%', transform: 'translateX(-50%)', bottom: 88, zIndex: 70, width: 'min(440px, calc(100vw - 32px))' }}>
      <Toast tone={toast.tone} onDismiss={clearToast}>{toast.message}</Toast>
    </div>
  );
}

/**
 * A real link around something that is otherwise just clickable.
 *
 * REPORTED, AND IT IS A FAIR COMPLAINT: "I am at the browse page and I can't
 * right-click and do open in a new tab. I use the middle-button click a lot."
 * Every listing was an `onClick` on an `<article>`, so the browser had no idea
 * it was a link — no middle-click, no ctrl-click, no context menu, no address in
 * the status bar on hover, and nothing for a keyboard or a screen reader either.
 *
 * WHAT THIS DOES NOT DO is take over navigation. A plain left click is still the
 * app's own routing, which is the whole reason the app feels instant; everything
 * the BROWSER does better is handed back to it:
 *
 *   middle click        a new tab, because that is what the button means
 *   ctrl / cmd click    a new tab
 *   shift click         a new window
 *   alt click           a download, if somebody really wants to
 *   right click         the ordinary menu, which needs no code at all — it works
 *                       the moment this is an `<a href>`
 *
 * `display: contents` so the anchor adds no box of its own. The grid below it
 * lays out the cards, not the links, and a wrapper with a layout would change
 * every grid it appears in.
 */
export function Linked({ to, children, style }) {
  const { go } = useApp();
  const href = hrefFor(to);
  return (
    <a
      href={href}
      style={{ display: 'contents', color: 'inherit', textDecoration: 'none', ...(style || {}) }}
      onClick={(e) => {
        // Anything but a plain left click belongs to the browser. `button` is 0
        // for the left one; a middle click arrives as an auxclick and never gets
        // here at all, which is exactly right.
        if (e.defaultPrevented) return;
        if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        go(to.name, to);
      }}
    >
      {children}
    </a>
  );
}

export function BackLink({ label }) {
  const { back, canBack, t } = useApp();
  if (!canBack) return null;
  // The default lives here rather than in the parameter list: a default parameter
  // is evaluated in the CALLER's scope, where the translator does not exist.
  return <Button variant="ghost" size="sm" iconLeft="arrow-left" onClick={back} style={{ marginLeft: -10 }}>{label || t("Back")}</Button>;
}

export function SectionHead({ title, sub, right }) {
  return (
    <div className="cbm-sect-head">
      <div><h1 className="cbm-h1">{title}</h1>{sub && <p className="cbm-sub">{sub}</p>}</div>
      {right}
    </div>
  );
}

export function StatRow({ items }) {
  return (
    <div style={{ display: 'flex', gap: 'var(--space-7)', flexWrap: 'wrap' }}>
      {items.map((s) => (
        <div key={s.label} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ font: 'var(--type-price)', color: 'var(--text-strong)' }}>{s.value}</span>
          <span className="cbm-label">{s.label}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * EVERY BULK-ACTIONS BAR IN THE PRODUCT, one component so "everywhere" stays
 * true: the shop's Stock, and the Selling tab and wants list on You.
 *
 * The owner, 2026-10-22, of design D's stock screen: "there's also a change in
 * the bulk actions container and buttons. I like that style better than what we
 * currently have. Can you implement it everywhere?" A sunken panel, one row on
 * a desktop: the label and the count on the left, the actions quiet and
 * outlined on the right. On a phone the actions take a row of their own under
 * the count, and the label is left off, as the phone design draws it.
 *
 * THE COUNT IS ALWAYS SAID, "0 selected" included, as the design draws it. It
 * is a number, not an instruction, and a count that comes and goes is a width
 * that comes and goes.
 *
 * The label is drawn and hidden from the accessibility tree: the region's own
 * name says "Bulk actions" once, and a screen reader hearing it twice is noise.
 *
 * What each caller keeps for itself is behaviour: `sticky` for the You lists,
 * which sit under the header while fifty rows scroll past; `lead`, a control
 * before the label (the phone's select-all box on Stock); `extra`, after the
 * count (Stock's "Clear", there only with a selection, and on the left so its
 * arrival moves none of the buttons); `note`, the reason a greyed button is
 * greyed. The buttons are the caller's own `Button`s, made quiet by app.css.
 */
export function BulkBar({ count, lead, extra, note, sticky = false, idle = false, className, children, ...rest }) {
  const { t } = useApp();
  const classes = ['cbm-bulk', sticky ? 'cbm-bulk--sticky' : '', idle ? 'cbm-bulk--idle' : '', className || '']
    .filter(Boolean).join(' ');
  return (
    <div role="region" aria-label={t("Bulk actions")} {...rest} className={classes}
         data-armed={count ? '1' : undefined}>
      {/* Held at a control's height only where something control-sized can arrive in it (Stock's "Clear"). */}
      <div className={`cbm-bulk__head${extra !== undefined ? ' cbm-bulk__head--holds' : ''}`}>
        {lead}
        <span className="cbm-label cbm-bulk__label" aria-hidden="true">{t("Bulk actions")}</span>
        {/* Its own sentence for one: "1 seleccionadas" was the bar's most common reading. */}
        <span className="cbm-bulk__n">{count === 1 ? t("1 selected") : t("{n} selected", { n: count })}</span>
        {extra}
      </div>
      <div className="cbm-bulk__acts">{children}</div>
      {note ? <span className="cbm-meta cbm-bulk__why">{note}</span> : null}
    </div>
  );
}

/**
 * Telling us something is broken.
 *
 * Open to guests on purpose. Somebody who cannot finish signing up, or who is
 * looking at a blank screen, is exactly the person whose report is worth most —
 * and requiring an account first is a filter that removes precisely those.
 *
 * The page is read from the router rather than typed, and the browser from the
 * request. Asking a person which page they were on is asking them to do the
 * diagnosis, and both answers are already in hand.
 */
export function SupportDialog({ open, onClose }) {
  const { trader, notify, t } = useApp();
  const [kind, setKind] = React.useState('bug');
  const [message, setMessage] = React.useState('');
  const [replyTo, setReplyTo] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => { if (open) { setKind('bug'); setMessage(''); setReplyTo(''); } }, [open]);
  if (!open) return null;

  const send = async () => {
    setBusy(true);
    try {
      await api.sendFeedback({
        message: message.trim(),
        kind,
        // Left out rather than sent empty: the field is an email or it is absent.
        ...(replyTo.trim() ? { replyTo: replyTo.trim() } : {}),
        page: (window.location.pathname + window.location.search).slice(0, 400),
      });
      notify(t("Thank you — we have it."));
      onClose();
    } catch (e) {
      notify(e && e.code === 'offline'
        ? t("You appear to be offline. Nothing was sent.")
        : t("That did not send. Try again in a moment."));
    } finally { setBusy(false); }
  };

  return (
    <ModalHost>
      <Dialog open closeLabel={t("Close")} width={520} onClose={onClose}
        title={t("Tell us what happened")}
        description={t("Bugs, ideas, anything. It reaches a person, not a queue.")}
        footer={<>
          <Button variant="ghost" onClick={onClose}>{t("Cancel")}</Button>
          <Button iconRight="send" disabled={busy || !message.trim()} onClick={send}>
            {busy ? t("Sending\u2026") : t("Send")}
          </Button>
        </>}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
          <Select label={t("What is this?")} value={kind} onChange={setKind}
            options={[
              { value: 'bug', label: t("Something is broken") },
              { value: 'idea', label: t("An idea") },
              { value: 'other', label: t("Something else") },
            ]} />
          <Textarea label={t("What happened?")} rows={5} value={message}
            onChange={(e) => setMessage(e.target.value.slice(0, 4000))}
            hint={t("What you did, what you expected, what you got instead.")} />
          {/*
            Only asked of guests. A signed-in trader's address is already on the
            account and re-typing it is a step that buys nothing — and it stays
            optional either way, because insisting on an address to report a bug
            is one more reason not to bother.
          */}
          {trader ? null : (
            <Input label={t("Email, if you would like an answer (optional)")} type="email"
              value={replyTo} onChange={(e) => setReplyTo(e.target.value)} />
          )}
          <span style={{ font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
            {t("We also record which page you were on and which browser you are using, so nobody has to describe them.")}
          </span>
        </div>
      </Dialog>
    </ModalHost>
  );
}

/**
 * The strip that makes support and the legal pages reachable.
 *
 * It exists because the site footer is `cbm-desk` — hidden entirely below 760px.
 * On a phone, which is most of this product's use, there was no route to the
 * privacy policy or the terms at all, and now no route to reporting a bug
 * either. Marked `cbm-mob` so it does not duplicate the desktop footer.
 */
export function SupportStrip() {
  const { locale, t } = useApp();
  const [open, setOpen] = React.useState(false);
  const link = { background: 'none', border: 0, padding: 0, cursor: 'pointer', font: 'var(--type-ui)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)', textDecoration: 'underline' };
  return (
    <>
      {/*
        The outer element sets NO display of its own. `.cbm-mob` is `display:none`
        at base and `display:revert` on mobile, and an inline style beats both —
        so an inline `display` here would pin the strip to one state forever. That
        has already shipped once on this codebase: a rule that was served, applied
        and inert, and cost an hour because the file was obviously correct. The
        layout goes on an inner element where nothing is fighting it.
      */}
      <div className="cbm-mob">
        <div style={{ display: 'flex', gap: 'var(--space-5)', flexWrap: 'wrap', justifyContent: 'center', padding: '0 var(--gutter-mobile) 96px' }}>
          <button type="button" style={link} onClick={() => setOpen(true)}>{t("Report a problem")}</button>
          {legalLinks(locale).map(([label, href]) => (
            <a key={href} href={href} style={link}>{label}</a>
          ))}
        </div>
      </div>
      <SupportDialog open={open} onClose={() => setOpen(false)} />
    </>
  );
}


/**
 * "Load more", and why it is a button rather than a scroll listener.
 *
 * Infinite scrolling takes the scrollbar away from the person: the page never
 * ends, so the footer — which is where the legal pages live — becomes
 * unreachable, and coming back to where you were means scrolling through
 * everything again. It also loads pages nobody asked for, on a connection
 * somebody may be paying for by the megabyte.
 *
 * A button is honest about what it costs and when it happens. It also degrades
 * to nothing: when there is no next page there is no control, so the end of the
 * list is visible rather than inferred from a spinner that stops.
 *
 * Numbered pages were the other option and are wrong here for a different
 * reason: they need a total, which for the browse feed means running a distance
 * filter twice to produce a number nobody reads — and page numbers move under
 * you as listings arrive.
 */
export function LoadMore({ cursor, busy, onClick, label }) {
  const { t } = useApp();
  if (!cursor) return null;
  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-6) 0' }}>
      <Button variant="outline" disabled={busy} onClick={onClick}>
        {busy ? t("Loading…") : (label || t("Show more"))}
      </Button>
    </div>
  );
}

/**
 * The shape of a listing grid, held while the first page is on its way.
 *
 * Three screens open on an empty list and fill it from the server — browse, and
 * both of the grids on your own page — and all three used to answer that moment
 * with one small box standing where a grid was about to appear. The screen went
 * chrome-and-a-box, then chrome-and-a-full-grid. Nothing about it was slow; the
 * JUMP was, and a jump is what gets reported as slowness.
 *
 * Here rather than three times over, because the three have to agree about the
 * grid class, the count and the announcement. A skeleton that disagreed with the
 * grid it replaces would reintroduce exactly the jump it exists to remove.
 *
 * HOW MANY. Two full rows at the widest grid (four columns), four on a phone
 * (two) — the first screenful at every breakpoint and a little past it, so the
 * fold is not a cliff. Deliberately not a page-worth: the request asks for
 * sixty, and sixty grey cards would promise a market this one does not have yet.
 * The three-column breakpoint leaves a last row of two, which is what a real
 * result set looks like, so it is left ragged rather than rounded up to twelve.
 *
 * `label` arrives already translated, like every other sentence a screen passes
 * down — the placeholders are aria-hidden, so this is the whole of what a screen
 * reader is told, and it should say which list is coming.
 */
const SKELETON_CARDS = 8;

export function ListingGridSkeleton({ label }) {
  return (
    <>
      <div className="cbm-grid-listings">
        {Array.from({ length: SKELETON_CARDS }, (_, i) => <Skeleton key={i} />)}
      </div>
      {/* Said once, not drawn eight times. "Eight cards are on their way" is not
          information; "loading" is. */}
      <span role="status" style={{ display: 'block', marginTop: 'var(--space-5)', font: 'var(--type-meta)', color: 'var(--text-faint)' }}>
        {label}
      </span>
    </>
  );
}

/**
 * The shape of a conversation, held while its messages are on their way.
 *
 * Opening a thread used to answer with the word "Loading…" in small grey type,
 * standing where a chat was about to be. That is the "box where a grid goes"
 * the browse skeleton replaced, on the screen where the wait is felt most: a
 * person opens a conversation because they want to read a reply.
 *
 * THE RHYTHM, which is the whole reason this is a table rather than a count.
 * Six identical rectangles down the left edge do not read as a conversation —
 * they read as a list, which is the wrong shape held confidently. Bubbles come
 * from two sides and are as long as what somebody said, so these alternate and
 * vary: 62% and 74% for the long ones, 30% for the "vale" at the end, and two
 * places where the same side speaks twice, because a strict left-right-left is
 * a metronome and nobody talks like one. It ends on your own side and short,
 * which is what the bottom of a live conversation usually looks like.
 *
 * The widths are percentages of the column, not pixels: the pane is a different
 * width on a phone and beside the inbox, and a bubble is a share of the line in
 * both.
 *
 * HOW MANY. Six. Enough to fill the top of a thread on a phone and to read as a
 * back-and-forth rather than a stack, and deliberately short of a screenful on a
 * desktop: the scroller opens at the BOTTOM, so a placeholder list longer than
 * the box would open scrolled past its own top and the sweep would start out of
 * sight. Nothing about the height of this matters to what surrounds it — the
 * thread header and the composer are pinned either side of the scroller — which
 * is exactly why six can be chosen for how it reads.
 *
 * `label` arrives already translated, like every other sentence a screen passes
 * down. The placeholders are aria-hidden, so this one line is the whole of what
 * a screen reader is told.
 */
const MESSAGE_SKELETON_ROWS = [
  ['flex-start', '62%'],
  ['flex-end', '44%'],
  ['flex-start', '74%'],
  ['flex-start', '38%'],
  ['flex-end', '56%'],
  ['flex-end', '30%'],
];

export function MessagesSkeleton({ label }) {
  return (
    <>
      {MESSAGE_SKELETON_ROWS.map(([side, width], i) => (
        <Skeleton key={i} shape="message" style={{ alignItems: side, '--cbm-skeleton-bubble': width }} />
      ))}
      {/* Said once, not drawn six times. */}
      <span role="status" style={{ font: 'var(--type-meta)', color: 'var(--text-faint)' }}>{label}</span>
    </>
  );
}

/**
 * The inbox list's shape, held while the conversations are on their way.
 *
 * The list had no loading branch at all: while the first request was in flight it
 * said "No messages yet" and offered a button to go and browse. That is the same
 * claim-before-asking the browse error branch and nearby's rows were written to
 * stop, on the screen people open precisely to find out whether anybody has
 * replied — and the empty state there is the more discouraging of the two
 * answers, so it is the worse one to guess at.
 *
 * HOW MANY. Five. Past the fold on a phone and short of a screenful on a desktop,
 * where the column is tall: an inbox is a list with no natural cap, so drawing a
 * page-worth of grey rows would promise a correspondence this marketplace does
 * not have yet. The same reasoning as nearby's five rather than its eight.
 *
 * THE WRAPPER IS NOT DECORATION. Each real row is a TraderRow inside a div that
 * carries its own hairline — the line under a row is drawn twice, and the row is
 * consequently a pixel taller than TraderRow alone. Wrapping the placeholder the
 * same way is what makes a stack of five exactly as tall as five real ones; a
 * skeleton that is a pixel short per row has produced a smaller version of the
 * jump it exists to remove. `aria-hidden` moves to the wrapper so the whole row
 * is one hidden thing rather than a bare div around a hidden one.
 *
 * `label` arrives already translated, like every other sentence a screen passes
 * down, and it is said once — five rows are not five announcements.
 */
const CONVERSATION_SKELETON_ROWS = 5;

export function ConversationRowsSkeleton({ label }) {
  return (
    <>
      {/*
        The rows go straight into the caller's stack rather than into one of
        their own. The inbox already IS a `cbm-stack` with `gap: 0`, and nesting a
        second one would put the placeholders a level deeper than the rows they
        stand in for — which is not a rendering difference but a structural one,
        and any measurement of the two states would then be comparing different
        elements.
      */}
      {Array.from({ length: CONVERSATION_SKELETON_ROWS }, (_, i) => (
        <div key={i} aria-hidden="true" style={{ borderBottom: '1px solid var(--border-hairline)' }}>
          <Skeleton shape="trader-row" />
        </div>
      ))}
      <span role="status" style={{ display: 'block', marginTop: 'var(--space-5)', font: 'var(--type-meta)', color: 'var(--text-faint)' }}>
        {label}
      </span>
    </>
  );
}

/**
 * The trades grid's shape, held while the trades are on their way.
 *
 * All three tabs answered the wait with an empty state — "No meetups agreed yet",
 * "Nothing waiting on a reply", "No finished trades yet" — each with a Browse
 * button under it. This is the screen where a false empty answer is most likely
 * to be believed, because most people genuinely do have no trades most of the
 * time, so "you have none" reads as confirmation rather than as news.
 *
 * HOW MANY. Two, which is one row of `cbm-grid-2` on a desktop and two rows on a
 * phone. A meetup card is tall — the placeholder alone is around 150px — so four
 * would be most of a laptop screen given over to grey, and a trades list is short
 * by nature: this is a marketplace where a busy month is a handful of hand-offs.
 * Deliberately not matched to the tab's eventual count, which nobody knows yet.
 *
 * The same `cbm-grid-2` as the real list, so the columns and the gaps cannot
 * become a second opinion about the layout.
 */
const TRADE_SKELETON_CARDS = 2;

export function MeetupCardsSkeleton({ label }) {
  return (
    <>
      <div className="cbm-grid-2">
        {Array.from({ length: TRADE_SKELETON_CARDS }, (_, i) => <Skeleton key={i} shape="meetup-card" />)}
      </div>
      <span role="status" style={{ display: 'block', marginTop: 'var(--space-5)', font: 'var(--type-meta)', color: 'var(--text-faint)' }}>
        {label}
      </span>
    </>
  );
}

/**
 * The wants list's shape, held while the wants are on their way.
 *
 * "No wants yet" was the third of these, said before anybody had asked — and it
 * sits beside the form that adds one, so the false answer arrives with an
 * invitation to act on it.
 *
 * HOW MANY. Three. A want is a card somebody is personally hunting for, and the
 * honest expectation is a couple rather than a shelf; the list also sits in the
 * left half of a split beside the "Add a want" card, so a long column of grey
 * would tower over the form it is meant to sit next to.
 *
 * `want-row` rather than `nearby-row`, though the two are nearly the same
 * silhouette — the padding and both type tokens differ, and reusing the near miss
 * would leave the stack about seven pixels short per row.
 *
 * ITS FIRST BOX IS A CONTROL NOW, not a decoration. The row's leading 18px is
 * the selection tick box rather than the bookmark icon it used to be, and the
 * placeholder is unchanged because the footprint is: 18px, same gap, same
 * column for the title. That is deliberate rather than lucky — selection was
 * drawn into the space the icon already held precisely so this shape, and the
 * test in `loading.spec.ts` that measures it, would keep holding.
 */
const WANT_SKELETON_ROWS = 3;

export function WantRowsSkeleton({ label }) {
  return (
    <>
      {Array.from({ length: WANT_SKELETON_ROWS }, (_, i) => <Skeleton key={i} shape="want-row" />)}
      <span role="status" style={{ display: 'block', marginTop: 'var(--space-5)', font: 'var(--type-meta)', color: 'var(--text-faint)' }}>
        {label}
      </span>
    </>
  );
}

/**
 * A row of chips where "All" is what an EMPTY selection looks like, with the
 * options most people want first and the rest behind one reveal.
 *
 * Three screens ask the same two questions — browse's filters, the profile, and
 * onboarding — and before this they each spelled the chips out again. The
 * duplication was not the problem; the divergence was. "All" arrived on the
 * languages in one place and not the games, and the two controls sat side by
 * side in onboarding reading as different kinds of question when they are the
 * same question on two axes.
 *
 * WHY A SHORT LIST. Seventeen language chips is five rows on a phone — eleven
 * was already three, before migration 053 — and on the
 * onboarding step that pushed Continue below the fold — a signup step whose
 * primary action is off screen is a step people abandon rather than scroll.
 * Games is five today and will grow, so it goes through the same control now
 * rather than being retrofitted the day a sixth arrives.
 *
 * NOTHING IS FETCHED. Both lists are constants that shipped with the page.
 * This is a reveal, and treating it as a load would add a request, a spinner and
 * a failure mode to seventeen strings that are already here.
 *
 * A LIT CHIP IS NEVER HIDDEN. If the selection holds anything outside the short
 * list — a saved preference, or a filter that arrived in a shared URL — the
 * whole list is shown from the first render. A filter that is on and invisible
 * is the worst state this control can be in: the results are wrong and the
 * reason is off screen, and nobody can debug what they cannot see.
 */
function ChipPicker({ options, shortList, selected, onToggle, onAll, allLabel, moreLabel }) {
  const { t } = useApp();
  // Expanding is sticky for as long as the screen lives. Collapsing the list back
  // under somebody mid-decision is a control that moves while being used.
  const [expanded, setExpanded] = React.useState(false);
  const short = new Set(shortList);
  const showAll = expanded || selected.some((value) => !short.has(value));
  const shown = showAll ? options : options.filter((o) => short.has(o.value));
  /*
   * The reveal appears only when it has something to reveal — asked of the LIST,
   * not of the expanded flag.
   *
   * Keyed on `showAll` it appeared on the games too, where the short list is the
   * whole list: a chip promising more, hiding nothing, and doing nothing visible
   * when tapped. The day a sixth game lands it starts earning its place with no
   * further change here, which is the point of routing games through this at all.
   */
  const somethingHidden = shown.length < options.length;
  return (
    <div className="cbm-wrap">
      <Tag selected={selected.length === 0} onClick={onAll}>{allLabel}</Tag>
      {shown.map((o) => (
        <Tag key={o.value} selected={selected.includes(o.value)} onClick={() => onToggle(o.value)}>{o.label}</Tag>
      ))}
      {somethingHidden ? <Tag onClick={() => setExpanded(true)}>{moreLabel || t("Show more…")}</Tag> : null}
    </div>
  );
}

/**
 * The games, with "All games" first.
 *
 * All five are in the short list, so nothing is hidden today and no reveal is
 * drawn. It goes through ChipPicker anyway: the order — One Piece leads — is a
 * product decision, and when a sixth game lands the control already knows what
 * to do with it instead of quietly growing a row on a phone.
 */
export function GamePicker({ selected, onToggle, onAll }) {
  const { t } = useApp();
  const options = GAMES.map((g) => ({ value: g.id, label: g.label }));
  return (
    <ChipPicker
      options={options}
      shortList={options.map((o) => o.value)}
      selected={selected} onToggle={onToggle} onAll={onAll}
      allLabel={t("All games")}
    />
  );
}

/**
 * The card languages, with "All languages" first.
 *
 * WHICH THREE COME FIRST is a rule, not a taste: your own interface language,
 * then the other one we speak, then Japanese. The first two are tied to
 * something real — who is reading — so a Spanish reader gets Spanish, English,
 * Japanese and an English reader gets English, Spanish, Japanese, and neither
 * has to expand anything to find their own. Japanese earns the third place
 * because Japanese prints are the common premium in Pokémon and One Piece; it is
 * the language people go looking for who are not looking for their own.
 *
 * The rest are sorted by the name the READER sees rather than by the stored tag,
 * like the picker on the posting form — alphabetical in your own language is the
 * only order you can scan.
 */
export function CardLanguagePicker({ selected, onToggle, onAll, games }) {
  const { t, locale } = useApp();
  const shortList = (locale || '').startsWith('es') ? ['es', 'en', 'ja'] : ['en', 'es', 'ja'];
  /*
   * ONLY LANGUAGES THE CHOSEN GAMES ARE PRINTED IN.
   *
   * One Piece has five, not seventeen: offering Hungarian for a card Bandai has
   * never printed in Hungarian is a filter that can only ever return nothing.
   *
   * `games` absent means no game chosen, which is EVERY language rather than
   * none — before somebody says what they collect we cannot know what is
   * impossible. Several games is the union, so ticking Magic alongside Pokémon
   * does not hide Thai.
   *
   * A language already SELECTED stays in the list whatever the games say. Browse
   * filters live in the URL and a shared link can carry both; dropping the chip
   * would leave a filter applied that the screen does not show — a reader
   * looking at an empty grid with no way to see why.
   */
  const allowed = languagesForGames(games);
  const visible = CARD_LANGUAGES.filter((code) => allowed.includes(code) || (selected || []).includes(code));
  const named = visible.map((code) => ({ value: code, label: t(CARD_LANGUAGE_NAMES[code]) }));
  const options = [
    // A short-list entry the chosen games do not print is simply absent.
    ...shortList.map((code) => named.find((o) => o.value === code)).filter(Boolean),
    ...named.filter((o) => !shortList.includes(o.value)).sort((a, b) => a.label.localeCompare(b.label)),
  ];
  return (
    <ChipPicker
      options={options}
      shortList={shortList}
      selected={selected} onToggle={onToggle} onAll={onAll}
      allLabel={t("All languages")}
    />
  );
}

/*
 * A percentage, as a seller types it and as the API carries it.
 *
 * The API carries BASIS POINTS — 100 = 1% — for the same reason it carries
 * cents: nothing about a price is ever a float, and "+2,5%" has to survive the
 * round trip. The conversion lives here, next to the box, rather than being a
 * rule every caller has to remember.
 *
 * A comma for the decimal, because that is what a Spanish keyboard produces and
 * what every other number on this screen uses. A typed dot is accepted too —
 * refusing it would be pedantry aimed at anybody with a numeric keypad.
 */
export function percentText(raw) {
  const cleaned = String(raw).replace(/[^0-9,.-]/g, '').replace(/\./g, ',');
  // One leading minus, one comma, at most two decimals.
  const negative = cleaned.startsWith('-');
  const digits = cleaned.replace(/-/g, '');
  const [whole = '', rest] = digits.split(',');
  const decimals = rest === undefined ? undefined : rest.slice(0, 2);
  return (negative ? '-' : '') + whole + (decimals === undefined ? '' : ',' + decimals);
}

/*
 * What the price will be, shown before it is.
 *
 * THE SAME THREE RULES THE SERVER APPLIES — multiply, add, never below a cent
 * — which is a duplication and is worth it: a switch that says "follow the low
 * price minus 5%" without ever showing the resulting number asks a seller to
 * trust a black box with their own inventory.
 *
 * The drift this risks is pinned down by an end-to-end test rather than by
 * hoping: it reads this preview, saves, and asserts the price that comes back
 * is the number that was shown. If the two rules ever disagree, that fails.
 *
 * The arithmetic is in floats here and in exact decimals on the server, so a
 * value landing exactly on a half-cent could in principle round the other way.
 * The server is what writes the price; this only has to be right enough to
 * decide with, and the test above is what says it is.
 */
export function previewCents(lowCents, adjust) {
  if (lowCents == null || lowCents <= 0) return null;
  const bp = adjust && adjust.unit === 'basisPoints' ? adjust.value : 0;
  const cents = adjust && adjust.unit === 'cents' ? adjust.value : 0;
  return Math.min(1000000_00, Math.max(1, Math.round(lowCents * (1 + bp / 10000) + cents)));
}

export function bpFromPercentText(text) {
  if (!text || text === '-') return null;
  const n = Number(String(text).replace(',', '.'));
  if (!Number.isFinite(n)) return null;
  // Rounded rather than truncated: 2,555 typed into a two-decimal box cannot
  // happen, but a float multiplication can still land on 254.99999999999997.
  return Math.round(n * 100);
}

export function percentTextFromBp(bp) {
  if (bp == null) return '';
  // Trailing zeros dropped: a seller who set 10% should see "10", not "10,00".
  return String(bp / 100).replace('.', ',');
}

/**
 * The market-following controls, for the two screens that offer them.
 *
 * ONE COPY BECAUSE TWO WOULD DRIFT. Posting a card and editing one ask exactly
 * the same question — which Cardmarket price to follow, and by how much — and a
 * second hand-written copy of three options, two bounds and a preview is the
 * kind of duplication that agrees on the common case and disagrees on the one
 * nobody tried. The rule behind it is shared on the server for the same reason;
 * this is that argument on the client.
 *
 * IT OWNS THE TEXT, THE CALLER OWNS THE ANSWER. A half-typed "-" or "1," is a
 * legitimate state on the way to a number and belongs in here; what leaves is
 * the parsed setting and whether it is usable, so a caller never has to know
 * that a percentage is carried as basis points.
 *
 * Renders NOTHING without a catalogue product. A card nobody matched has no
 * market price to follow, and a disabled switch invites a hunt for whatever
 * would enable it — see the note at the call sites.
 */
export function MarketFollowFields({ lowCents, avgCents, initial, onChange, note }) {
  const { t } = useApp();
  const [follows, setFollows] = React.useState(!!(initial && initial.followsMarket));
  const start = (initial && initial.adjust) || null;
  const [kind, setKind] = React.useState(
    !start ? 'none' : start.unit === 'basisPoints' ? 'percent' : 'money',
  );
  const [percent, setPercent] = React.useState(
    start && start.unit === 'basisPoints' ? percentTextFromBp(start.value) : '',
  );
  /*
   * The money adjustment keeps its sign separate from its digits.
   *
   * `api.moneyText` formats what somebody is typing into an amount and has no
   * concept of a negative one — reasonably, since no price is negative. An
   * adjustment is the one number here that goes either way, so the direction is
   * a choice rather than a character they have to remember to type.
   */
  /*
   * WHICH Cardmarket number — migration 092, and the same two words the
   * posting form offers as one-tap suggestions. A seller who taps
   * "Cardmarket · average" and then switches following on should not have to
   * work out that the setting calls it something else.
   */
  const [basis, setBasis] = React.useState((initial && initial.basis) || 'low');
  const [below, setBelow] = React.useState(!!(start && start.unit === 'cents' && start.value < 0));
  const [money, setMoney] = React.useState(
    start && start.unit === 'cents'
      ? api.moneyText(String(Math.abs(start.value) / 100).replace('.', ','))
      : '',
  );

  const bp = kind === 'percent' ? bpFromPercentText(percent) : null;
  const cents = kind === 'money'
    ? (api.centsFromMoney(money) > 0 ? api.centsFromMoney(money) * (below ? -1 : 1) : null)
    : null;
  const adjust = kind === 'percent' && bp !== null ? { unit: 'basisPoints', value: bp }
    : kind === 'money' && cents !== null ? { unit: 'cents', value: cents }
    : null;
  /*
   * THE SAME BOUNDS THE SERVER AND THE TABLE USE, said here first.
   *
   * Not belt and braces: this runs unattended overnight across a whole
   * inventory, and "why is everything eleven times the price" is a question
   * nobody should have to ask on a Tuesday morning. The CHECK guarantees it and
   * the schema refuses it; this is what makes the refusal a sentence beside the
   * box rather than an error after saving.
   */
  // The number the chosen basis names, which is what the preview is about.
  const followed = basis === 'avg' ? avgCents : lowCents;
  const percentBad = follows && kind === 'percent' && (bp === null || bp < -9900 || bp > 100000);
  const moneyBad = follows && kind === 'money' && cents === null;

  // Reported upward on every change rather than read out of here by the parent,
  // so a caller cannot forget to ask and publish a listing with a setting the
  // seller can see on their own screen.
  const report = onChange;
  React.useEffect(() => {
    /*
     * THE RESULTING PRICE GOES UP WITH THE SETTINGS.
     *
     * Reported: "it's weird that I see 'Cardmarket: €0.02 You charge €0.04' but
     * then I don't see the price as 0.04." Quite right — on the posting form
     * the listing really IS born at this number, so a price box showing
     * something else is the screen contradicting itself while both halves look
     * authoritative.
     *
     * Null when there is nothing to follow, which is a different answer from
     * zero: a card the source does not price keeps whatever the seller typed,
     * and a caller must be able to tell those apart.
     */
    report({
      followsMarket: follows, adjust, basis,
      priceCents: follows ? previewCents(followed, adjust) : null,
      valid: !percentBad && !moneyBad,
    });
    // The adjustment is compared by value: rebuilding an equal object must not
    // re-report, or this runs on every keystroke that changes nothing.
    // `followed` is in the deps because the price it produces is: the foil box
    // changes WHICH pair of numbers applies without changing anything else here.
  }, [follows, basis, followed, adjust ? adjust.unit : null, adjust ? adjust.value : null, percentBad, moneyBad]);

  return (
    /*
      A STABLE HANDLE, because the words are not one. The Selling tab now has a
      BUTTON labelled "Follow Cardmarket's price" that opens the bulk dialog,
      and this card's own heading says the same thing — so a test looking for
      the text finds two things and fails on a page that is perfectly correct.
      Tests scope to this instead.
    */
    <Card data-testid="market-follow" tone="sunken" padding="var(--space-5)" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
        <div style={{ flex: 1 }}>
          <div style={{ font: 'var(--type-body)', fontSize: 'var(--text-sm)', color: 'var(--text-strong)' }}>
            {t("Follow Cardmarket's price")}
          </div>
          <div style={{ font: 'var(--type-meta)', color: 'var(--text-muted)', marginTop: 2 }}>
            {t("Updated every day from Cardmarket.")}
          </div>
        </div>
        <Switch checked={follows} onChange={setFollows} />
      </div>

      {follows ? (
        <>
          {/*
            WHICH NUMBER FIRST, then what to do to it. The order is the
            sentence a seller is building: "follow Cardmarket's average price,
            minus five per cent".

            AND THE OPTIONS BELOW SAY "that price", not "the low price". They
            did say it, from before the average existed, which left a seller
            reading "the low price, adjusted by a percentage" directly under a
            selector they had just set to the average. Naming the basis twice
            means one of the two names is wrong half the time.
          */}
          <Select
            label={t("Which price to follow")}
            value={basis}
            onChange={setBasis}
            options={[
              { value: 'low', label: t("Cardmarket's low price") },
              { value: 'avg', label: t("Cardmarket's average price") },
            ]}
          />
          <Select
            label={t("What to charge")}
            value={kind}
            onChange={setKind}
            options={[
              { value: 'none', label: t("That price, as it is") },
              { value: 'percent', label: t("That price, adjusted by a percentage") },
              { value: 'money', label: t("That price, adjusted by an amount") },
            ]}
          />

          {kind === 'percent' ? (
            <Input
              label={t("Percentage")}
              suffix="%"
              inputMode="decimal"
              wrapperStyle={{ maxWidth: 200 }}
              value={percent}
              onChange={(e) => setPercent(percentText(e.target.value))}
              error={percentBad ? t("Give a percentage between -99 and 1000.") : undefined}
              /* A minus is the whole point of the field, so it is said rather
                 than left to be discovered. */
              hint={t("Start with a minus to sell below the market: -5 means 5% cheaper.")}
            />
          ) : null}

          {kind === 'money' ? (
            <div style={{ display: 'flex', gap: 'var(--space-4)', alignItems: 'flex-start' }}>
              <Select
                label={t("Direction")}
                value={below ? 'below' : 'above'}
                onChange={(v) => setBelow(v === 'below')}
                options={[
                  { value: 'above', label: t("Above that price") },
                  { value: 'below', label: t("Below that price") },
                ]}
              />
              <Input
                label={t("Amount")}
                suffix="€"
                inputMode="decimal"
                wrapperStyle={{ maxWidth: 160 }}
                value={money}
                onChange={(e) => setMoney(api.moneyText(e.target.value))}
                error={moneyBad ? t("Give an amount above 0€.") : undefined}
              />
            </div>
          ) : null}

          {/*
            THE NUMBER, before it is the number.

            Where the catalogue prices this card the seller sees both what
            Cardmarket says and what they will be charging, which is the whole
            decision they are making. Where it does not, the honest sentence is
            that nothing will happen — said here rather than discovered
            tomorrow.
          */}
          {/*
            THE NUMBER THE CHOSEN BASIS ACTUALLY PRODUCES. Both travel from the
            server so switching between them costs no request — and so the
            preview can keep up with the foil box, which changes WHICH pair of
            numbers applies without changing the card.

            A basis the source does not price falls through to the sentence
            below rather than showing the other one: "you charge X" under a
            label saying average, computed from the low, is the kind of quiet
            wrongness this whole feature has to avoid.
          */}
          {followed != null && followed > 0 ? (
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
              <span style={{ font: 'var(--type-meta)', color: 'var(--text-muted)' }}>
                {t("Cardmarket: {price}", { price: api.formatCents(followed) })}
              </span>
              <span data-testid="market-preview" style={{ font: 'var(--type-body)', fontSize: 'var(--text-sm)', color: 'var(--text-strong)' }}>
                {t("You charge {price}", { price: api.formatCents(previewCents(followed, adjust)) })}
              </span>
            </div>
          ) : (
            /*
              No single price to preview. One card the catalogue does not price,
              or a whole selection of different cards — `note` is how the caller
              says which, because "this card" is the wrong noun for a hundred of
              them and a sentence that does not fit what is on screen is worse
              than none.
            */
            <span style={{ font: 'var(--type-meta)', color: 'var(--text-muted)' }}>
              {note || t("If Cardmarket has no price for this card, yours stays as it is.")}
            </span>
          )}
        </>
      ) : null}
    </Card>
  );
}
